import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper, Button } from '@mui/material';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { documentService } from '../../services/api';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * DocumentViewerRedirect - A page that handles document viewing from email links.
 * 
 * When user clicks a document link in an email:
 * 1. If authenticated - fetches SAS URL and redirects to document
 * 2. If not authenticated - redirects to login, then back here after login
 */
export default function DocumentViewerRedirect() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      if (!documentId) {
        setError('Invalid document ID');
        setLoading(false);
        return;
      }

      if (!isAuthenticated) {
        // Store the current URL to return after login
        sessionStorage.setItem('postLoginRedirect', window.location.pathname);
        // Trigger login
        try {
          await instance.loginRedirect({
            scopes: ['openid', 'profile', 'email']
          });
        } catch (err) {
          console.error('Login failed:', err);
          setError('Please log in to view this document.');
          setLoading(false);
        }
        return;
      }

      // User is authenticated - fetch document URL
      try {
        const result = await documentService.getDocumentUrl(documentId);
        if (result?.sasUrl) {
          // Redirect to the SAS URL
          window.location.replace(result.sasUrl);
        } else {
          setError('Document URL not available');
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to get document URL:', err);
        if (err?.response?.status === 404) {
          setError('Document not found');
        } else if (err?.response?.status === 403) {
          setError('You do not have permission to view this document');
        } else {
          setError('Failed to load document. Please try again.');
        }
        setLoading(false);
      }
    };

    fetchAndRedirect();
  }, [documentId, isAuthenticated, instance]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        gap: 2
      }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading document...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        p: 3
      }}>
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Unable to Open Document
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
          >
            Go to Home
          </Button>
        </Paper>
      </Box>
    );
  }

  return null;
}

